import { useCallback } from "react";

import { useToast, UseToastOptions } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { AudioSound, useAudio } from "shared/hooks/useAudio";
import { IntlMessageId } from "shared/lang";

export type UseErrorToastWithAudioArgs = {
  title?: IntlMessageId;
  disableDoubleToast?: boolean;
  /**
   * When true and a toast with the same id is already active, replace its content
   * via toast.update() instead of dropping the new call. Opt-in to avoid changing
   * behavior for existing callers.
   */
  replaceIfActive?: boolean;
} & Omit<UseToastOptions, "title">;

export function useErrorToastWithAudio({ errorToastId }: { errorToastId: string }) {
  const { play: playErrorAudio } = useAudio(AudioSound.error);
  const toast = useToast();
  const intl = useIntl();
  const showToastAndPlayError = useCallback(
    (args: UseErrorToastWithAudioArgs = {}) => {
      playErrorAudio();

      const { title, disableDoubleToast = true, replaceIfActive = false, ...rest } = args;
      const formattedTitle = title
        ? intl.formatMessage({ id: title })
        : intl.formatMessage({ id: "hooks.use-custom-toast.error-title" });

      const toastOptions: UseToastOptions = {
        id: errorToastId,
        title: formattedTitle,
        isClosable: true,
        status: "error",
        containerStyle: {
          alignItems: "center",
        },
        ...rest,
      };

      if (toast.isActive(errorToastId)) {
        if (replaceIfActive) {
          toast.update(errorToastId, toastOptions);
          return;
        }
        if (disableDoubleToast) {
          return;
        }
      }
      toast(toastOptions);
    },
    [errorToastId, intl, playErrorAudio, toast],
  );

  const closeIfToastActive = useCallback(
    () => {
      if (toast.isActive(errorToastId)) {
        toast.close(errorToastId);
      }
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errorToastId],
  );

  return {
    showToastAndPlayError,
    closeIfToastActive,
  };
}
